import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  SPARKLINE_DEFAULT_BAR_RADIUS,
  SPARKLINE_DEFAULT_GAP,
  SPARKLINE_DEFAULT_HEIGHT,
  SPARKLINE_DEFAULT_MIN_BAR_FRACTION,
  SPARKLINE_TONE_DEFAULT,
  type SparklineTone,
} from './Sparkline.constants';
import {
  Bar,
  BarButton,
  Root,
  Tip,
  TipLabel,
  TipValue,
} from './Sparkline.styled';

export interface SparklineBin {
  /**
   * The numeric value scaled into the chart. Negative values are clamped
   * to zero; non-finite values (NaN/Infinity) are treated as zero.
   */
  value: number;
  /**
   * Short header for the tooltip + base for the bar's accessible name
   * (e.g. `"Mon, Apr 14"` → screen reader hears "Mon, Apr 14: 8 runs").
   */
  label?: string;
  /**
   * Override the entire tooltip body. When provided, the default
   * `<label /><value />` rendering is replaced by this node.
   */
  tooltip?: ReactNode;
}

export interface SparklineProps {
  /**
   * The histogram data. Each entry is one bar; pass `label` and (optionally)
   * `tooltip` to enrich the per-bar tooltip + accessible name. The simplest
   * call is `bins={values.map(v => ({ value: v }))}`.
   */
  bins: SparklineBin[];
  /**
   * Total chart height in pixels. Bars stretch from baseline up to this
   * height. Defaults to 32 — a typical "inline analytics card" slot.
   */
  height?: number;
  /** Horizontal gap between bars in pixels. Defaults to 2. */
  gap?: number;
  /** Top-corner radius applied to each bar in pixels. Defaults to 1. */
  barRadius?: number;
  /**
   * Minimum bar height as a fraction of the chart height (0–1). Empty bins
   * still render at this height so the chart shows the full timeline rather
   * than appearing to have gaps. Pass `0` to disable. Defaults to 0.08
   * (≈8%).
   */
  minBarFraction?: number;
  /**
   * Bar colour intent. Defaults to `default` (primary surface accent). Use
   * a semantic tone when the chart carries a status read at a glance.
   */
  tone?: SparklineTone;
  /**
   * When false, the chart renders as a static thumbnail — no focusable
   * bars, no tooltips, no click handler. Use this for read-only previews
   * (e.g. a row's mini-chart inside a list). Defaults to `true`.
   */
  interactive?: boolean;
  /**
   * Format the numeric value for display in the tooltip + accessible name.
   * Defaults to `String(value)` — pass a richer formatter for percentages,
   * compact units (`"1.2K"`), durations, etc.
   */
  formatValue?: (value: number, bin: SparklineBin, index: number) => string;
  /**
   * Fired when the user activates a bar (mouse click, Enter, or Space).
   * Use this to drill into the underlying record (e.g. open the run for
   * that day).
   */
  onBarClick?: (bin: SparklineBin, index: number) => void;
  /**
   * Fired whenever the active (hovered/focused) bar changes. `null` means
   * no bar is active. Useful for syncing a sibling chart or surfacing the
   * value in a parent header.
   */
  onActiveChange?: (bin: SparklineBin | null, index: number | null) => void;
  /**
   * Accessible name for the chart as a whole. Each bar additionally gets
   * its own per-bin label (see {@link SparklineBin.label}).
   */
  ariaLabel?: string;
  className?: string;
  dataTestId?: string;
}

/**
 * Compact bar-histogram chart with hover/focus tooltips and optional
 * click-through. One bar per bin; the active bar (set by hover or keyboard
 * focus) brightens and a single floating tooltip slides to its column to
 * show the underlying data.
 *
 * Renders as plain `<button>`s with `currentColor`-tinted backgrounds so it
 * works without an SVG runtime, recolours from the parent (just set the
 * parent's colour), and is keyboard-navigable (Tab between bars, Esc to
 * dismiss the tooltip).
 *
 * @example
 *   <Sparkline
 *     bins={runs.map(r => ({
 *       value: r.count,
 *       label: format(r.day, 'EEE, MMM d'),
 *     }))}
 *     tone="success"
 *     ariaLabel="Runs per day, last 14 days"
 *     formatValue={(n) => `${n} runs`}
 *     onBarClick={(_, i) => openDay(i)}
 *   />
 *
 * Edge cases handled:
 *   • Empty `bins` → renders a flat empty chart frame (no bars, but the
 *     container stays so layout doesn't shift).
 *   • All-zero `bins` → every bar is the minimum-fraction stub.
 *   • Negative / NaN / Infinity values → treated as 0.
 */
const Sparkline: React.FC<SparklineProps> = ({
  bins,
  height = SPARKLINE_DEFAULT_HEIGHT,
  gap = SPARKLINE_DEFAULT_GAP,
  barRadius = SPARKLINE_DEFAULT_BAR_RADIUS,
  minBarFraction = SPARKLINE_DEFAULT_MIN_BAR_FRACTION,
  tone = SPARKLINE_TONE_DEFAULT,
  interactive = true,
  formatValue = defaultFormatValue,
  onBarClick,
  onActiveChange,
  ariaLabel = 'Sparkline',
  className,
  dataTestId = 'sparkline',
}) => {
  const safeMinFraction = clamp01(minBarFraction);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Sanitise once and reuse for BOTH the heights AND the
  // tooltip/aria-label values. Previously the labels read raw
  // `bin.value`, which would announce `NaN`/`Infinity`/negative numbers
  // even though the rendered bar had collapsed to the minBarFraction
  // stub — a confusing mismatch for SR users.
  const sanitisedValues = useMemo(
    () =>
      bins.map((b) =>
        Number.isFinite(b.value) && b.value > 0 ? b.value : 0,
      ),
    [bins],
  );

  const heightsPct = useMemo(() => {
    const max = sanitisedValues.reduce((m, v) => (v > m ? v : m), 0);
    if (max === 0) {
      return sanitisedValues.map(() => safeMinFraction * 100);
    }
    const minPct = safeMinFraction * 100;
    return sanitisedValues.map((v) => Math.max((v / max) * 100, minPct));
  }, [sanitisedValues, safeMinFraction]);

  const updateActive = useCallback(
    (next: number | null) => {
      setActiveIndex((prev) => {
        if (prev === next) return prev;
        const nextBin = next !== null ? bins[next] ?? null : null;
        onActiveChange?.(nextBin, next);
        return next;
      });
    },
    [bins, onActiveChange],
  );

  // Esc dismisses the tooltip — standard tooltip semantics, mirrors the
  // `Tooltip` atom. Listener is only mounted while the tip is open.
  useEffect(() => {
    if (!interactive || activeIndex === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') updateActive(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [interactive, activeIndex, updateActive]);

  const handleRootMouseLeave = useCallback(() => {
    updateActive(null);
  }, [updateActive]);

  const handleBarBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      // Only clear when focus leaves the chart entirely. Tab between bars
      // would otherwise blur-then-focus and momentarily hide the tip.
      if (!rootRef.current?.contains(e.relatedTarget as Node | null)) {
        updateActive(null);
      }
    },
    [updateActive],
  );

  const showTip = interactive && activeIndex !== null && bins.length > 0;
  const activeBin = activeIndex !== null ? bins[activeIndex] : null;
  // Centre the tooltip horizontally on the active bar's midpoint. Bars
  // share the chart width equally, so bar `i` of `n` is centred at
  // `((i + 0.5) / n) * 100%`.
  const tipLeftPct =
    activeIndex !== null && bins.length > 0
      ? ((activeIndex + 0.5) / bins.length) * 100
      : 0;

  return (
    <Root
      ref={rootRef}
      $height={height}
      $gap={gap}
      $tone={tone}
      // `role="img"` is correct for the static thumbnail variant — the
      // chart is a single piece of decorative imagery with one
      // accessible name. In the interactive variant the chart is a
      // *group* of focusable bars; stamping `role="img"` there would
      // mask the per-bar buttons from assistive tech. Use `group` with
      // an aria-label so the chart still reads as a labelled cluster
      // without hiding its children.
      role={interactive ? 'group' : 'img'}
      aria-label={ariaLabel}
      className={className}
      onMouseLeave={interactive ? handleRootMouseLeave : undefined}
      data-testid={dataTestId}
      data-tone={tone}
      data-bins={bins.length}
      data-interactive={interactive || undefined}
    >
      {heightsPct.map((heightPct, idx) => {
        const bin = bins[idx];
        const barStyle: React.CSSProperties = {
          height: `${heightPct}%`,
          borderRadius: `${barRadius}px ${barRadius}px 0 0`,
        };

        if (!interactive) {
          return (
            <Bar
              // Index is stable here — the array semantics are positional
              // (one bar per bin in order), not identity-based.
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              style={barStyle}
              data-testid={`${dataTestId}__bar`}
              data-bar-index={idx}
            />
          );
        }

        // Use the sanitised value so the label / formatted string
        // mirrors what the user actually sees (a 0-height stub announces
        // "0", not "NaN"). The original `bin` is still passed to
        // `formatValue` so consumers retain access to siblings like
        // `bin.label`.
        const safeValue = sanitisedValues[idx];
        const formatted = formatValue(safeValue, bin, idx);
        const accessibleName = bin.label
          ? `${bin.label}: ${formatted}`
          : formatted;

        return (
          <BarButton
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            type="button"
            style={barStyle}
            $isActive={activeIndex === idx}
            onMouseEnter={() => updateActive(idx)}
            onFocus={() => updateActive(idx)}
            onBlur={handleBarBlur}
            onClick={onBarClick ? () => onBarClick(bin, idx) : undefined}
            aria-label={accessibleName}
            data-testid={`${dataTestId}__bar`}
            data-bar-index={idx}
            data-active={activeIndex === idx || undefined}
          />
        );
      })}
      {interactive && bins.length > 0 && (
        <Tip
          $isOpen={showTip}
          style={{ left: `${tipLeftPct}%` }}
          role="tooltip"
          aria-hidden={!showTip}
          data-testid={`${dataTestId}__tip`}
        >
          {activeBin &&
            renderTipBody(
              activeBin,
              sanitisedValues[activeIndex ?? 0] ?? 0,
              activeIndex ?? 0,
              formatValue,
            )}
        </Tip>
      )}
    </Root>
  );
};

const renderTipBody = (
  bin: SparklineBin,
  /** Sanitised value (≥0, finite) — see Sparkline component above. */
  safeValue: number,
  index: number,
  formatValue: NonNullable<SparklineProps['formatValue']>,
): ReactNode => {
  if (bin.tooltip !== undefined) return bin.tooltip;
  return (
    <>
      {bin.label && <TipLabel>{bin.label}</TipLabel>}
      <TipValue>{formatValue(safeValue, bin, index)}</TipValue>
    </>
  );
};

const defaultFormatValue = (value: number): string => String(value);

const clamp01 = (n: number): number => {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return 1;
  return n;
};

export default Sparkline;
